// Agent Command — command stage (commit_2).
//
// api.commands.* (the canonical dispatcher, COMMANDS registry in
// @square/core/commands) covers project/workspace/site/hosting/comms
// navigation and actions — it has no agent-directed command (no "ask
// agent X to draft…", no "pause agent X" verb in its syntax table). The
// only real, governed path for an agent-directed action is the v23
// Agent Workspace PR-style request: prepare -> operator approves ->
// executes through the owning gate. This stage is a typed front end for
// exactly that endpoint (api.agentWorkspace.actionRequests.create) —
// not a second dispatcher, not a fabricated third path.

import { useState, type CSSProperties } from 'react'
import type { RegistryAgent } from '../../../api/client'
import type { AgentWorkspaceLoadState, AgentWorkspaceSummary } from './useAgentWorkspaces'
import { AGENT_ACTION_TYPES, actionTypeOption, type AgentActionTypeValue } from './actionTypes'
import { AGENT_CARD, AGENT_FONT, AGENT_STATUS_ACCENT, AGENT_VOID } from './theme'
import { AGENT_ACTION_CREATE_MIN_LEVEL, AGENT_ACTION_DECIDE_MIN_LEVEL } from './permissions'

export interface StageFormInput {
  agentId: string
  actionType: AgentActionTypeValue
  title: string
  summary: string
  targets: string
}

export type StageSubmissionState =
  | { status: 'idle' }
  | { status: 'submitting' }
  | { status: 'created'; requestId: string; workspaceName: string; actionType: string }
  | { status: 'unresolved_targets'; unresolved: Array<{ input: string; reason: string | null }> }
  | { status: 'error'; message: string }

export interface AgentCommandStageProps {
  agents: RegistryAgent[]
  roleLevel: number | null

  workspaceState: AgentWorkspaceLoadState
  workspaceErrorMessage: string | null
  targetWorkspace: AgentWorkspaceSummary | null
  creatingWorkspace: boolean
  createWorkspaceError: string | null
  onCreateWorkspace: (name: string) => void

  submission: StageSubmissionState
  onSubmit: (input: StageFormInput) => void
}

const label: CSSProperties = {
  display: 'block',
  fontFamily: AGENT_FONT.mono,
  fontSize: 8.5,
  fontWeight: 700,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  color: AGENT_VOID.faint,
  marginBottom: 4,
}

const controlStyle: CSSProperties = {
  width: '100%',
  background: 'rgba(4,7,10,0.6)',
  border: `1px solid ${AGENT_VOID.hairline}`,
  borderRadius: 8,
  color: AGENT_VOID.ink,
  fontFamily: AGENT_FONT.body,
  fontSize: 12.5,
  padding: '8px 10px',
  boxSizing: 'border-box',
}

const fieldWrap: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4 }

export default function AgentCommandStage({
  agents,
  roleLevel,
  workspaceState,
  workspaceErrorMessage,
  targetWorkspace,
  creatingWorkspace,
  createWorkspaceError,
  onCreateWorkspace,
  submission,
  onSubmit,
}: AgentCommandStageProps) {
  const [agentId, setAgentId] = useState('')
  const [actionType, setActionType] = useState<AgentActionTypeValue>('draft_email')
  const [title, setTitle] = useState('')
  const [summary, setSummary] = useState('')
  const [targets, setTargets] = useState('')

  const option = actionTypeOption(actionType)
  const submitting = submission.status === 'submitting'

  const permission: 'unknown' | 'allowed' | 'denied' =
    roleLevel === null ? 'unknown' : roleLevel >= AGENT_ACTION_CREATE_MIN_LEVEL ? 'allowed' : 'denied'

  const workspaceReady = workspaceState === 'ready'
  const hasWorkspace = !!targetWorkspace
  const formDisabled = !workspaceReady || !hasWorkspace || permission === 'denied' || submitting || !title.trim() || !summary.trim()

  let disabledReason: string | null = null
  if (!workspaceReady) disabledReason = 'Waiting on your Agent Workspace rooms to load.'
  else if (!hasWorkspace) disabledReason = 'Create an Agent Workspace room first (button above).'
  else if (permission === 'denied') disabledReason = `Your role does not meet the required level (${AGENT_ACTION_CREATE_MIN_LEVEL}).`
  else if (!title.trim() || !summary.trim()) disabledReason = 'Title and summary are required — the request must explain what will happen.'

  // Pre-computed as single strings (not interleaved JSX text/expression
  // children) so each renders as one text node — avoids the SSR
  // text-boundary comment React inserts between adjacent text-like
  // siblings, and keeps the sentence readable in one place.
  const approvalRequiredText = `Approval required: every request enters the queue as pending_approval — it does not execute until an operator other than you (role level ${AGENT_ACTION_DECIDE_MIN_LEVEL}) decides it below.`
  const permissionAllowedText = `Permission preview: your role (level ${roleLevel}) meets the required level (${AGENT_ACTION_CREATE_MIN_LEVEL}) to prepare a request.`
  const permissionDeniedText = `Permission preview: your role (level ${roleLevel}) does not meet the required level (${AGENT_ACTION_CREATE_MIN_LEVEL}) — the server will refuse this request.`
  const receiptText = submission.status === 'created'
    ? `id ${submission.requestId} · workspace ${submission.workspaceName} · ${submission.actionType} · awaiting operator approval in the queue below.`
    : ''

  const submit = () => {
    if (formDisabled) return
    onSubmit({ agentId, actionType, title: title.trim(), summary: summary.trim(), targets })
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Workspace resolution — explicit about which room a request lands in.
          A single, persistently-rendered aria-live wrapper announces the
          async loading -> ready/auth/forbidden/error transition to screen
          readers, matching the roster + approval-queue panels' pattern. */}
      <div aria-live="polite" aria-atomic="false" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {workspaceState === 'loading' && <p style={{ color: AGENT_VOID.muted, fontSize: 12.5 }}>Loading your Agent Workspace rooms…</p>}
      {workspaceState === 'auth_required' && <p style={{ color: AGENT_VOID.muted, fontSize: 12.5 }}>Sign in to prepare an agent action request.</p>}
      {workspaceState === 'forbidden' && <p style={{ color: AGENT_VOID.muted, fontSize: 12.5 }}>Your role does not have access to Agent Workspace.</p>}
      {workspaceState === 'error' && (
        <p style={{ color: AGENT_STATUS_ACCENT.red, fontSize: 12.5 }}>Could not load your Agent Workspace rooms: {workspaceErrorMessage}</p>
      )}
      {workspaceReady && !targetWorkspace && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <p style={{ color: AGENT_VOID.muted, fontSize: 12.5, margin: 0 }}>No Agent Workspace room exists yet.</p>
          <button
            type="button"
            onClick={() => onCreateWorkspace('Agent Command')}
            disabled={creatingWorkspace}
            title={creatingWorkspace ? 'Creating your Agent Workspace room…' : 'Create the Agent Workspace room this request will land in'}
            style={{
              fontFamily: AGENT_FONT.mono,
              fontSize: 9.5,
              fontWeight: 800,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              padding: '6px 12px',
              borderRadius: 7,
              border: `1px solid ${AGENT_STATUS_ACCENT.cyan}66`,
              color: AGENT_STATUS_ACCENT.cyan,
              background: `${AGENT_STATUS_ACCENT.cyan}12`,
              cursor: creatingWorkspace ? 'not-allowed' : 'pointer',
              opacity: creatingWorkspace ? 0.6 : 1,
            }}
          >
            {creatingWorkspace ? 'Creating…' : 'Create Agent Workspace room'}
          </button>
        </div>
      )}
      {createWorkspaceError && <p style={{ color: AGENT_STATUS_ACCENT.red, fontSize: 11.5 }}>Could not create the workspace: {createWorkspaceError}</p>}
      {targetWorkspace && (
        <p style={{ fontFamily: AGENT_FONT.mono, fontSize: 8.5, color: AGENT_VOID.faint, margin: 0 }}>
          Preparing into workspace: <span style={{ color: AGENT_VOID.muted }}>{targetWorkspace.name}</span>
        </p>
      )}
      </div>{/* /workspace-resolution aria-live */}

      {/* Grid columns + breakpoint live in the shared <style> block
          (AgentCommandCenter.tsx, .sqv2-agents-stagegrid) so the
          620px collapse-to-1fr rule can win over an inline style. */}
      <div className="sqv2-agents-stagegrid">
        <div style={fieldWrap}>
          <label style={label} htmlFor="agent-command-stage-agent">Target agent (registry-selected)</label>
          <select id="agent-command-stage-agent" value={agentId} onChange={(e) => setAgentId(e.target.value)} style={controlStyle}>
            <option value="">No preparing agent (operator-only request)</option>
            {agents.map((a) => (
              <option key={a.agent_id} value={a.agent_id}>
                {`${a.display_name} (${a.agent_id})`}
              </option>
            ))}
          </select>
        </div>
        <div style={fieldWrap}>
          <label style={label} htmlFor="agent-command-stage-type">Action type</label>
          <select
            id="agent-command-stage-type"
            value={actionType}
            onChange={(e) => setActionType(e.target.value as AgentActionTypeValue)}
            style={controlStyle}
          >
            {AGENT_ACTION_TYPES.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div style={fieldWrap}>
        <label style={label} htmlFor="agent-command-stage-title">Title</label>
        <input
          id="agent-command-stage-title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. Draft welcome email for new resident"
          style={controlStyle}
        />
      </div>

      <div style={fieldWrap}>
        <label style={label} htmlFor="agent-command-stage-summary">Summary (what will happen)</label>
        <textarea
          id="agent-command-stage-summary"
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
          rows={2}
          placeholder="Explain exactly what this request does — shown to the approver."
          style={{ ...controlStyle, resize: 'vertical' as CSSProperties['resize'] }}
        />
      </div>

      {option?.needsTargets && (
        <div style={fieldWrap}>
          <label style={label} htmlFor="agent-command-stage-targets">Targets (comma-separated emails)</label>
          <input
            id="agent-command-stage-targets"
            value={targets}
            onChange={(e) => setTargets(e.target.value)}
            placeholder="resident@example.com, ops@example.com"
            style={controlStyle}
          />
        </div>
      )}

      {/* Execution mode + approval-required indicator — always honest, never claims direct execution */}
      {option && (
        <div style={{ ...AGENT_CARD, padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ fontFamily: AGENT_FONT.mono, fontSize: 8.5, color: AGENT_VOID.faint, lineHeight: 1.6 }}>{option.executionMode}</div>
          <div style={{ fontFamily: AGENT_FONT.mono, fontSize: 8.5, color: AGENT_STATUS_ACCENT.violet, lineHeight: 1.6 }}>
            {approvalRequiredText}
          </div>
        </div>
      )}

      {/* Permission preview */}
      <div style={{ fontFamily: AGENT_FONT.mono, fontSize: 9, lineHeight: 1.6 }}>
        {permission === 'unknown' && <span style={{ color: AGENT_VOID.faint }}>Permission preview: your role has not loaded yet — the server checks again on submit.</span>}
        {permission === 'allowed' && <span style={{ color: AGENT_STATUS_ACCENT.green }}>{permissionAllowedText}</span>}
        {permission === 'denied' && <span style={{ color: AGENT_STATUS_ACCENT.red }}>{permissionDeniedText}</span>}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={submit}
          disabled={formDisabled}
          title={disabledReason ?? 'Prepare this action request — it will await operator approval'}
          aria-describedby={disabledReason ? 'agent-command-stage-disabled-reason' : undefined}
          style={{
            fontFamily: AGENT_FONT.mono,
            fontSize: 10,
            fontWeight: 800,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            padding: '8px 16px',
            borderRadius: 8,
            border: 'none',
            color: formDisabled ? AGENT_VOID.faint : '#04140c',
            background: formDisabled ? 'rgba(150,140,220,0.08)' : AGENT_STATUS_ACCENT.green,
            cursor: formDisabled ? 'not-allowed' : 'pointer',
            opacity: formDisabled ? 0.55 : 1,
          }}
        >
          {submitting ? 'Preparing…' : 'Prepare request'}
        </button>
        {disabledReason && (
          <span id="agent-command-stage-disabled-reason" style={{ fontFamily: AGENT_FONT.mono, fontSize: 8.5, color: AGENT_VOID.faint }}>
            {disabledReason}
          </span>
        )}
      </div>

      {/* Correlation / receipt state — persistently-rendered aria-live
          wrapper announces the submit idle -> created/unresolved/error
          transition to screen readers. */}
      <div aria-live="polite" aria-atomic="false" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {submission.status === 'created' && (
        <div style={{ ...AGENT_CARD, borderColor: `${AGENT_STATUS_ACCENT.green}55`, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontFamily: AGENT_FONT.mono, fontSize: 9, fontWeight: 800, color: AGENT_STATUS_ACCENT.green, textTransform: 'uppercase' }}>
            Request prepared
          </span>
          <span style={{ fontFamily: AGENT_FONT.mono, fontSize: 8.5, color: AGENT_VOID.muted }}>{receiptText}</span>
        </div>
      )}
      {submission.status === 'unresolved_targets' && (
        <div style={{ ...AGENT_CARD, borderColor: `${AGENT_STATUS_ACCENT.amber}55`, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontFamily: AGENT_FONT.mono, fontSize: 9, fontWeight: 800, color: AGENT_STATUS_ACCENT.amber, textTransform: 'uppercase' }}>
            Unresolved targets — not created
          </span>
          {submission.unresolved.map((t, i) => (
            <span key={`${t.input}-${i}`} style={{ fontFamily: AGENT_FONT.mono, fontSize: 8.5, color: AGENT_VOID.muted }}>
              {t.input}: {t.reason ?? 'could not be resolved'}
            </span>
          ))}
        </div>
      )}
      {submission.status === 'error' && (
        <div style={{ ...AGENT_CARD, borderColor: `${AGENT_STATUS_ACCENT.red}55` }}>
          <span style={{ fontFamily: AGENT_FONT.mono, fontSize: 9, color: AGENT_STATUS_ACCENT.red }}>Request failed: {submission.message}</span>
        </div>
      )}
      </div>{/* /submission-result aria-live */}
    </div>
  )
}
